import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ApiError, api } from '../lib/api';
import { Banner, Button, Field, Mark, input } from '../lib/ui';
import { t } from '../lib/i18n';

/**
 * Accepting an invitation, without a session.
 *
 * The link proves possession, not identity, so accepting does not sign anyone in: it
 * creates the account and then sends them through the normal login, which is the step
 * that proves the password.
 */
interface Preview {
  email: string;
  tenant: string;
  user_exists: boolean;
}

export function AcceptInvite({ token, onDone }: { token: string; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const preview = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api<Preview>(`/v1/invitations/peek?token=${encodeURIComponent(token)}`),
    retry: false,
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/v1/invitations/accept', { method: 'POST', body: { token, password, name } });
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError && err.problem.errors?.some((x) => x.code === 'too_short')
          ? t('Das Passwort braucht mindestens 12 Zeichen.')
          : t('Die Einladung konnte nicht angenommen werden. Vielleicht ist sie abgelaufen.'),
      );
    } finally {
      setBusy(false);
    }
  }

  const card = 'w-full max-w-sm space-y-4 rounded-[6px] border border-[var(--color-line)] bg-[var(--color-surface)] p-8';

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className={card}>
        <div className="flex items-center gap-2">
          <Mark size={20} />
          <span className="text-[15px] font-semibold tracking-tight">Consent</span>
          <span className="text-[15px] text-[var(--color-muted)]">Console</span>
        </div>

        {preview.isLoading && <p className="text-sm text-[var(--color-muted)]">{t('Wird geprüft …')}</p>}

        {preview.isError && (
          <Banner kind="error">
            {t('Diese Einladung ist nicht (mehr) gültig. Bitten Sie um eine neue.')}
          </Banner>
        )}

        {done && (
          <>
            <Banner kind="ok">{t('Konto angelegt. Melden Sie sich jetzt an.')}</Banner>
            <Button onClick={onDone}>{t('Zur Anmeldung')}</Button>
          </>
        )}

        {preview.data && !done && (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm">
              {t('{tenant} lädt Sie als {email} ein.',
                { tenant: preview.data.tenant, email: preview.data.email })}
            </p>
            {error && <Banner kind="error">{error}</Banner>}

            {preview.data.user_exists ? (
              <p className="text-sm text-[var(--color-muted)]">
                {t('Für diese Adresse gibt es bereits ein Konto. Nach dem Annehmen melden Sie sich wie gewohnt an.')}
              </p>
            ) : (
              <>
                <Field label={t('Name')}>
                  <input className={input} value={name} onChange={(e) => setName(e.target.value)} />
                </Field>
                <Field label={t('Passwort')} hint={t('Mindestens 12 Zeichen.')}>
                  <input className={input} type="password" autoComplete="new-password" required
                    value={password} onChange={(e) => setPassword(e.target.value)} />
                </Field>
              </>
            )}
            <Button type="submit" disabled={busy}>
              {busy ? t('Wird angenommen …') : t('Einladung annehmen')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
